// src/features/chat/views/GroupChatInterface.jsx
/*
 * Legacy compatibility:
 * - Handles legacy message types (search_results/draft_post/post) no longer emitted by Genkit.
 * - Retained only for backward compatibility with existing data.
 * - Remove after legacy data migration/cleanup is complete.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isYesterday, formatDistanceToNow } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Image as ImageIcon } from 'lucide-react';
import { auth } from '../../../firebase';

import {
  useAssistantChat,
  groupTarget,
  MessageList,
  PendingAssistantBubble,
  Composer,
} from '..';
import {
  GroupHeader,
  GroupSettings,
  resetUnreadCount as markGroupMessagesAsRead,
  setMyGroupNotificationsEnabled,
  subscribeToGroup,
  subscribeToMembership,
  toggleGroupBotResponses,
} from '../../groups';
import ChatPostList from '../../../components/ChatPostList';
import DraftItem from '../../../components/DraftItem';
import PostMessage from '../../../components/PostMessage';
import RecordMessage from '../components/RecordMessage';
import RecordDraftMessage from '../components/RecordDraftMessage';
import RecordCitationMarkdown from '../components/RecordCitationMarkdown';
import MessageActions from '../components/MessageActions';
import ReplyIndicator from '../components/ReplyIndicator';
import SwipeableMessage from '../components/SwipeableMessage';
import ChatImagePreviewModal from '../components/ChatImagePreviewModal';
import botIcon from '../../../assets/icon.png';
import useFilterStore from '../../records/stores/useFilterStore';
import { useChatSenderProfiles } from '../hooks/useChatSenderProfiles';
import useDelayedTrue from '../hooks/useDelayedTrue';
import { SenderMeta, getSenderDisplay } from '../components/SenderMeta';
import { useIsMobileLike } from '../../../hooks/useIsMobileLike';
import { logCustomEvent, setAnalyticsUserProperties } from '../../../services/analytics/analytics';
import { ANALYTICS_EVENTS } from '../../../services/analytics/events';

import layoutStyles from '../styles/ChatLayout.module.css';
import messageStyles from '../styles/Message.module.css';
import listStyles from '../styles/MessageList.module.css';
import groupStyles from './GroupChatInterface.module.css';

const MESSAGES_PER_PAGE = 50;

const toTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value.toDate === 'function') return value;
  if (value instanceof Date) return Timestamp.fromDate(value);
  return null;
};

const timestampToDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts instanceof Date) return ts;
  return null;
};

const shouldShowDateSeparator = (prevTs, currentTs) => {
  const currentDate = timestampToDate(currentTs);
  if (!currentDate) return false;
  const prevDate = timestampToDate(prevTs);
  if (!prevDate) return true;
  return currentDate.toDateString() !== prevDate.toDateString();
};

const MessageTimestamp = ({ timestamp, variant }) => {
  const date = timestampToDate(timestamp);
  if (!date) return null;

  const minutesAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60));
  const timeString =
    minutesAgo < 60
      ? formatDistanceToNow(date, { addSuffix: true })
      : format(date, 'h:mm a');

  return (
    <span className={groupStyles.timestamp} data-variant={variant}>
      {timeString}
    </span>
  );
};

const DateSeparator = ({ current, previous, t }) => {
  if (!shouldShowDateSeparator(previous, current)) return null;

  const date = timestampToDate(current);
  if (!date) return null;

  const label = isToday(date)
    ? t('chat.today')
    : isYesterday(date)
    ? t('chat.yesterday')
    : format(date, 'MMMM d');

  return (
    <div className={groupStyles.dateSeparator}>
      <div className={groupStyles.dateSeparatorContent}>{label}</div>
    </div>
  );
};

const ReplyPreview = ({ inReplyTo, lookupMessage, t, variant }) => {
  const replyTo = inReplyTo ? lookupMessage(inReplyTo) : null;
  if (!replyTo) return null;

  const handleReplyClick = () => {
    const messageElement = document.getElementById(`message-${replyTo.id}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <button
      type="button"
      className={groupStyles.replyPreview}
      data-variant={variant}
      onClick={handleReplyClick}
    >
      <div className={groupStyles.replyLine} data-variant={variant} />
      <div className={groupStyles.replyContent}>
        {replyTo.images?.length > 0 && (
          <ImageIcon size={14} className={groupStyles.replyImageIcon} />
        )}
        <span className={groupStyles.replyText}>
          {replyTo.text || t('chat.reply_placeholder', 'Replied message')}
        </span>
      </div>
    </button>
  );
};

const GroupChatInterface = ({ groupId, initialGroup, onBack }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setFilterScope = useFilterStore((state) => state.setScope);
  const isMobileLike = useIsMobileLike();

  // Initialize group from prop - component remounts on groupId change via key={groupId}
  const [group, setGroup] = useState(initialGroup ?? null);

  const [membershipStatus, setMembershipStatus] = useState('loading');
  const [membershipData, setMembershipData] = useState(null);
  const membership = membershipStatus === 'ready' ? membershipData : null;

  // Derived membership state
  const joinedAt = membership?.joinedAt ?? null;
  const isAdmin = membership?.role === 'admin';
  const notificationsEnabled = membership?.notificationsEnabled ?? true;
  const botResponsesEnabled = membership?.botResponsesEnabled ?? true;

  const [showSettings, setShowSettings] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [previewContext, setPreviewContext] = useState(null);
  const [localError, setLocalError] = useState(null);

  const locale = i18n?.resolvedLanguage || i18n?.language;
  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions()?.timeZone;
    } catch (_error) {
      return undefined;
    }
  }, []);

  const target = useMemo(() => {
    if (!groupId) return groupTarget('placeholder', null);
    return groupTarget(groupId, joinedAt);
  }, [groupId, joinedAt]);

  const chat = useAssistantChat(target, {
    pageSize: MESSAGES_PER_PAGE,
    locale,
    timezone,
  });
  const { profiles: senderProfiles } = useChatSenderProfiles(chat.messages);
  const { getMessageById } = chat;

  const replyingToMessage = replyingToId ? getMessageById(replyingToId) : null;
  const handlePreviewReply = useCallback(() => {
    if (!previewContext?.messageId) return;
    setReplyingToId(previewContext.messageId);
    setPreviewContext(null);
  }, [previewContext, setPreviewContext, setReplyingToId]);

  useEffect(() => {
    if (!groupId) return undefined;

    const unsubscribe = subscribeToGroup(
      groupId,
      (updatedGroup) => {
        setGroup(updatedGroup);
        setLocalError(null);
      },
      (error) => {
        console.error('Error syncing group data:', error);
        setLocalError(t('chat.load_error'));
      }
    );

    return () => unsubscribe();
  }, [groupId, t]);

  useEffect(() => {
    if (!groupId) return undefined;
    setMembershipStatus('loading');
    setMembershipData(null);
    setLocalError(null);

    const unsubscribe = subscribeToMembership(
      groupId,
      (memberData) => {
        setMembershipData({
          joinedAt: toTimestamp(memberData.joinedAt),
          role: memberData.role,
          notificationsEnabled: memberData.notificationsEnabled,
          botResponsesEnabled: memberData.botResponsesEnabled,
        });
        setMembershipStatus('ready');
        setLocalError(null);
      },
      (error) => {
        console.error('Error fetching member data:', error);
        setMembershipData(null);
        setMembershipStatus('error');
        setLocalError(error?.message || t('chat.load_error'));
      }
    );

    return () => unsubscribe();
  }, [groupId, t]);

  useEffect(() => {
    if (!groupId || membershipStatus !== 'ready') return;
    // Only mark as read after first snapshot loads (not during connecting/loading)
    if (!chat.initialLoaded || chat.messages.length === 0) return;
    markGroupMessagesAsRead(groupId).catch((err) =>
      console.error('Error marking group messages as read:', err)
    );
  }, [groupId, membershipStatus, chat.initialLoaded, chat.messages.length]);

  const handleShowAllPosts = useCallback(() => {
    if (groupId) {
      setFilterScope({ mode: 'group', groupId });
      const params = new URLSearchParams({ scope: 'group', gid: groupId });
      navigate(`/records/notes?${params.toString()}`);
    } else {
      navigate('/records');
    }
  }, [groupId, navigate, setFilterScope]);

  const handleSend = useCallback(
    async ({ text, files }) => {
      if (!groupId || membershipStatus !== 'ready') return;
      setLocalError(null);

      try {
        await chat.send({
          text,
          files,
          replyTo: replyingToId,
          enableAssistant: botResponsesEnabled,
        });

        logCustomEvent(ANALYTICS_EVENTS.SEND_GROUP_MESSAGE, {
          group_id: groupId,
          message_length: text?.trim()?.length || 0,
          image_count: files?.length || 0,
          language: i18n.language,
        });
        setAnalyticsUserProperties({ has_sent_group_message: 'true' });
        setReplyingToId(null);
      } catch (err) {
        console.error('Error sending group message:', err);
        setLocalError(t('chat.send_error'));
        throw err;
      }
    },
    [botResponsesEnabled, chat, groupId, i18n.language, membershipStatus, replyingToId, t]
  );

  const handleToggleNotifications = useCallback(async () => {
    if (!groupId || membershipStatus !== 'ready' || !membership) return;
    const next = !notificationsEnabled;
    try {
      const success = await setMyGroupNotificationsEnabled(groupId, next);
      if (success) {
        setMembershipData((prev) => prev && { ...prev, notificationsEnabled: next });
      }
    } catch (err) {
      console.error('Error toggling notifications:', err);
      setLocalError(t('groups.notification_toggle_error'));
    }
  }, [groupId, membership, membershipStatus, notificationsEnabled, t]);

  const handleToggleBotResponses = useCallback(async () => {
    if (!groupId || membershipStatus !== 'ready' || !membership) return;
    const next = !botResponsesEnabled;
    try {
      const success = await toggleGroupBotResponses(groupId, next);
      if (success) {
        setMembershipData((prev) => prev && { ...prev, botResponsesEnabled: next });
      }
    } catch (err) {
      console.error('Error toggling bot responses:', err);
      setLocalError(t('groups.bot_toggle_error'));
    }
  }, [groupId, membership, membershipStatus, botResponsesEnabled, t]);

  const combinedError = localError || chat.error;

  const renderMessage = useCallback(
    (message, index) => {
      const isCurrentUser = message.senderId === auth.currentUser?.uid;
      const isAssistant = message.role === 'assistant';
      const variant = isCurrentUser ? 'sent' : 'received';
      const senderInfo = getSenderDisplay(
        message,
        senderProfiles,
        t('chat.user'),
        t('app_name')
      );
      const previousMessage = chat.messages[index - 1];
      const isFirstInSequence =
        index === 0 || previousMessage?.senderId !== message.senderId;
      const showSender = !isCurrentUser && isFirstInSequence;
      const supportsReply = message.uiType === 'chat';
      const canReply = supportsReply && !isCurrentUser;

      const handleStartReply = () => setReplyingToId(message.id);

      const renderContent = () => {
        // Legacy message types (search_results/draft_post/post) are no longer emitted by Genkit.
        // Retained only for backward compatibility with existing data; remove after legacy data migration/cleanup.
        switch (message.uiType) {
          case 'search_results':
            return (
              <div className={messageStyles.cardSlot}>
                <ChatPostList postIds={message.postIds} />
              </div>
            );
          case 'draft_post':
            return (
              <div className={messageStyles.cardSlot}>
                <DraftItem draftId={message.draftId} />
              </div>
            );
          case 'record_draft':
            return (
              <div className={messageStyles.cardSlot}>
                <RecordDraftMessage draftId={message.draftId} />
              </div>
            );
          case 'post':
            return (
              <div className={messageStyles.cardSlot}>
                <PostMessage postId={message.postId} />
              </div>
            );
          case 'record':
            return (
              <div className={messageStyles.cardSlot}>
                <RecordMessage recordId={message.recordId} />
              </div>
            );
          default:
            return (
              <div className={messageStyles.bubble}>
                {message.inReplyTo && (
                  <ReplyPreview
                    inReplyTo={message.inReplyTo}
                    lookupMessage={getMessageById}
                    t={t}
                    variant={variant}
                  />
                )}

                {message.images?.length > 0 &&
                  message.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img.url}
                      alt={img.name || t('chat.uploaded_image', { index: idx + 1 })}
                      className={`${messageStyles.image} ${messageStyles.imageInteractive}`}
                      loading="lazy"
                      onClick={() =>
                        setPreviewContext({
                          url: img.url,
                          images: message.images,
                          initialIndex: idx,
                          senderName: senderInfo.displayName,
                          senderAvatar: senderInfo.avatarUrl,
                          timestamp: message.timestamp,
                          messageId: message.id,
                          canReply,
                        })
                      }
                    />
                  ))}

                {message.text && (
                  isAssistant ? (
                    <RecordCitationMarkdown
                      text={message.text}
                      recordCitations={message.recordCitations}
                    />
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {message.text}
                    </ReactMarkdown>
                  )
                )}

                <MessageTimestamp timestamp={message.timestamp} variant={variant} />

                {!isMobileLike && canReply && (
                  <MessageActions
                    onReply={handleStartReply}
                    variant={variant}
                    visible={hoveredMessageId === message.id}
                  />
                )}
              </div>
            );
        }
      };

      const messageRow = (
        <div
          id={`message-${message.id}`}
          className={messageStyles.row}
          data-variant={variant}
          data-has-avatar={showSender ? 'true' : 'false'}
          onMouseEnter={
            !isMobileLike && canReply ? () => setHoveredMessageId(message.id) : undefined
          }
          onMouseLeave={
            !isMobileLike && canReply
              ? () =>
                  setHoveredMessageId((prev) =>
                    prev === message.id ? null : prev
                  )
              : undefined
          }
        >
          {showSender && (
            <SenderMeta
              sender={senderInfo}
              botIcon={botIcon}
              botName={t('app_name')}
              avatarProps={{ textColor: 'var(--background-elevated)' }}
            />
          )}

          {renderContent()}
        </div>
      );

      return (
        <React.Fragment key={message.id}>
          <DateSeparator
            current={message.timestamp}
            previous={previousMessage?.timestamp}
            t={t}
          />

          {isMobileLike && canReply ? (
            <SwipeableMessage onReply={handleStartReply} variant={variant}>
              {messageRow}
            </SwipeableMessage>
          ) : (
            messageRow
          )}
        </React.Fragment>
      );
    },
    [
      getMessageById,
      chat.messages,
      senderProfiles,
      t,
      isMobileLike,
      hoveredMessageId,
      setPreviewContext,
    ]
  );

  // Raw loading signal: membership or messages are still loading, and no messages yet
  const isLoadingRaw =
    (membershipStatus === 'loading' ||
      (membershipStatus === 'ready' && !chat.initialLoaded)) &&
    chat.messages.length === 0 &&
    !combinedError;

  // Apply delayed-true policy to avoid flicker on fast loads
  const showLoading = useDelayedTrue(isLoadingRaw, 200);

  // Empty state: membership and messages are fully loaded and there are none
  const showEmptyState =
    membershipStatus === 'ready' &&
    chat.initialLoaded &&
    chat.messages.length === 0 &&
    !combinedError;

  // Early return only for truly missing groupId
  if (!groupId) return null;

  const replyingSender = replyingToMessage
    ? getSenderDisplay(replyingToMessage, senderProfiles, t('chat.user'), t('app_name'))
    : null;
  const composerPlaceholder =
    membershipStatus === 'loading'
      ? t('chat.connecting', 'Connecting...')
      : membershipStatus === 'error'
      ? t('chat.cannot_connect', 'Cannot connect right now')
      : replyingToMessage
      ? t('chat.reply_placeholder')
      : t('chat.type_message');

  return (
    <div className={`${layoutStyles.surface} ${groupStyles.root}`}>
      <GroupHeader
        group={group ?? { id: groupId, name: t('common.loading') }}
        onBack={onBack}
        onOpenSettings={
          membershipStatus === 'ready' ? () => setShowSettings(true) : undefined
        }
        onToggleBotResponses={
          membershipStatus === 'ready' ? handleToggleBotResponses : undefined
        }
        botResponsesEnabled={botResponsesEnabled}
        onShowAllPosts={handleShowAllPosts}
      />

      {combinedError && <div className={layoutStyles.notice}>{combinedError}</div>}
      {chat.rateLimit && (
        <div className={layoutStyles.notice}>
          {chat.rateLimit.message}
          {chat.rateLimit.resetAt && (
            <span>
              {' '}
              {t('chat.try_again_at', {
                time: chat.rateLimit.resetAt.toLocaleTimeString(),
              })}
            </span>
          )}
        </div>
      )}

      <MessageList
        messages={chat.messages}
        hasMore={chat.hasMore}
        loadingMore={chat.loadingMore}
        onLoadMore={chat.loadMore}
        renderMessage={renderMessage}
      >
        {/* Initial loading state - show before messages arrive */}
        {showLoading && (
          <div className={listStyles.timelineLoading}>
            <div className={listStyles.timelineSpinner} />
            <span>{t('chat.loading_messages', 'Loading messages...')}</span>
          </div>
        )}

        {/* Empty state - only after confirmed empty */}
        {showEmptyState && (
          <div className={groupStyles.emptyChatState}>
            {t('chat.no_messages_yet', 'No messages yet')}
          </div>
        )}

        {chat.pendingAssistant && (
          <PendingAssistantBubble
            pending={chat.pendingAssistant}
            botIcon={botIcon}
            botName={t('app_name')}
          />
        )}

        {chat.pendingDrafts?.map((draft) => (
          <div
            key={`pending-draft-${draft.draftId}`}
            className={messageStyles.row}
            data-variant="received"
            data-has-avatar="true"
          >
            <SenderMeta
              sender={{ displayName: t('app_name') || 'Assistant', isAssistant: true }}
              botIcon={botIcon}
              botName={t('app_name') || 'Assistant'}
            />
            <div className={messageStyles.cardSlot}>
              <RecordDraftMessage draftId={draft.draftId} />
            </div>
          </div>
        ))}
      </MessageList>

      <div>
        {replyingToMessage && (
          <ReplyIndicator
            message={replyingToMessage}
            sender={replyingSender}
            onCancel={() => setReplyingToId(null)}
          />
        )}

        <Composer
          onSend={handleSend}
          disabled={membershipStatus !== 'ready' || !!chat.rateLimit}
          sending={chat.sending}
          placeholder={composerPlaceholder}
        />
      </div>

      {showSettings && group && (
        <GroupSettings
          group={group}
          onClose={() => setShowSettings(false)}
          onUpdate={(updatedGroup) => setGroup(updatedGroup)}
          onLeaveGroup={() => navigate('/groups')}
          notificationsEnabled={notificationsEnabled}
          onToggleNotifications={handleToggleNotifications}
          isAdmin={isAdmin}
        />
      )}

      {previewContext && (
        <ChatImagePreviewModal
          imageUrl={previewContext.url}
          images={previewContext.images}
          initialIndex={previewContext.initialIndex}
          onClose={() => setPreviewContext(null)}
          onReply={previewContext.canReply ? handlePreviewReply : undefined}
          senderName={previewContext.senderName}
          timestamp={previewContext.timestamp}
          senderAvatar={previewContext.senderAvatar}
          showContext={Boolean(previewContext.senderName)}
        />
      )}
    </div>
  );
};

export default GroupChatInterface;
